/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  MarketingPageElementLocation,
  MarketingPageElementType,
  i18nFunct,
  localizePhoneNumbers,
  lucidColors,
  sendBeaconEvent,
  useSiteMetadata
} from '@lucid/gatsby-theme-lucid';
import { graphql, useStaticQuery } from 'gatsby';
import Cookies from 'js-cookie';
import kebabCase from 'lodash.kebabcase';
import throttle from 'lodash.throttle';
import {
  Box,
  Button,
  Navbar as DSNavbar,
  NavbarWrapper
} from 'lucid-components-react';
import { NavbarContext } from 'lucid-components-react/dist/lib/components/navbar/navbar';
import { FC, useContext, useEffect, useState } from 'react';
import LazyLoad, { forceCheck } from 'react-lazyload';

import Link from 'components/link/link';
import LayoutContext from 'contexts/layoutContext';
import useMarketoLink from 'hooks/use-marketo-link';
import { useMenuAddons } from 'hooks/use-menu';
import useNavButtons, {
  placeholderButtons as initialPlaceholderButtons
} from 'hooks/use-nav-buttons';
import { MenuLink } from 'types/types';
import { isBrowser } from 'utils/helpers';
import './navbar.scss';

const LOGO =
  'https://d2slcw3kip6qmk.cloudfront.net/marketing/images/LucidLogo_Website.svg';

export interface LinkInterface extends MenuLink {
  type?: string;
  primary?: boolean;
  secondary?: boolean;
  truncate?: boolean;
  isButton?: boolean;
  isPlaceholder?: boolean;
  links: LinkInterface[];
}

interface Props {
  alwaysScrolled?: boolean;
  menu: LinkInterface[];
  scrollDistance?: number;
  isHomepage?: boolean;
  hideSignUpButton?: boolean;
  isVisio?: boolean;
  isDiscovery?: boolean;
}

enum LinkType {
  DROPDOWN,
  MARKETO,
  LINK,
  LABEL,
  PLACEHOLDER
}

const getLinkType = (link: LinkInterface, isTopLevel: boolean) => {
  let linkType = LinkType.LINK;

  if (link.links.length > 0) {
    if (isTopLevel) {
      linkType = LinkType.DROPDOWN;
    } else {
      linkType = LinkType.LABEL;
    }
  } else {
    if (link.fieldMarketo) {
      linkType = LinkType.MARKETO;
    }
    if (link.isPlaceholder) {
      linkType = LinkType.PLACEHOLDER;
    }
  }

  return linkType;
};

const Navbar: FC<Props> = ({
  alwaysScrolled = true,
  hideSignUpButton,
  isDiscovery,
  isVisio,
  menu,
  scrollDistance = 0
}) => {
  const { language } = useContext(LayoutContext);
  const getMarketoLink = useMarketoLink(language);

  const { shortLangPrefix, langPrefix, i18nKey } = language;

  // Slicing the first four because beyond that the layout would break
  const addonLinks = useMenuAddons(langPrefix).slice(0, 4) as MenuLink[];

  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_nav_menu
              }
            }
          }
        }
      }
    `
  );

  const i18n = i18nFunct(i18nData, i18nKey);

  const { title } = useSiteMetadata();

  const LOGO_LINK = `/${shortLangPrefix === 'en' ? '' : shortLangPrefix}`;

  const username = isBrowser()
    ? window.atob(Cookies.get('username') || '')
    : undefined;
  const visioQueryParams = '?type=visio';

  const navButtons = useNavButtons(
    username,
    isVisio,
    isDiscovery,
    hideSignUpButton
  );
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  let placeholderButtons = initialPlaceholderButtons;
  if (hideSignUpButton) {
    placeholderButtons = initialPlaceholderButtons.slice(1);
  }

  useEffect(() => {
    forceCheck();
  }, [isOpen]);

  useEffect(() => {
    const handleScroll = throttle(() => {
      const distance = scrollDistance;
      setIsScrolled(window.pageYOffset > distance);
    }, 100);

    if (scrollDistance !== undefined) {
      window.addEventListener('scroll', handleScroll);
      handleScroll();
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollDistance]);

  const showImage = (i: number) => {
    if (i === 0 || i === 1) {
      return addonLinks.length === 2;
    } else if (i === 2) {
      return addonLinks.length === 3;
    }

    return false;
  };

  const navbarBoxShadow = '2px 2px 2px rgba(196, 196, 196, 0.5)';
  const mobileBreakpoint = '440px';

  const renderItem = (
    link: LinkInterface,
    isSubmenuLink?: boolean,
    isButton?: boolean,
    isTopLevel = true
  ) => {
    const type = getLinkType(link, isTopLevel);

    const renderMarketoButton = ({
      fieldKmEventName,
      fieldMarketo,
      title: marketoTitle
    }: LinkInterface) => {
      const mktoId = fieldMarketo?.entity.fieldMarketoId;
      const { url, copy } = getMarketoLink(mktoId);

      return (
        <Link data-km-trigger={`nav-${kebabCase(fieldKmEventName)}`} to={url}>
          {marketoTitle || copy}
        </Link>
      );
    };

    const renderLink = (link: LinkInterface, isSubmenuLink?: boolean) => {
      let url =
        link.fieldLinkPhoneNumber && link.fieldLinkPhoneNumber.trim().length
          ? localizePhoneNumbers(`tel:${link.fieldLinkPhoneNumber}`)
          : link.link.url!.path;
      url =
        isVisio && link.fieldKey === 'pricing'
          ? `${url}${visioQueryParams}`
          : url;

      let beaconElementType;
      if (link.isButton) {
        beaconElementType = MarketingPageElementType.buttonsecondary;
      } else if (isSubmenuLink) {
        beaconElementType = MarketingPageElementType.linkdropdown;
      } else {
        beaconElementType = MarketingPageElementType.link;
      }

      const beaconEvent = link.fieldBeaconElementDetail
        ? {
          name: 'clickedMarketingPageElement',
          elementLocation: MarketingPageElementLocation.nav,
          elementDetail: link.fieldBeaconElementDetail,
          elementType: beaconElementType
        }
        : null;

      return (
        <Link
          key={link.entityId || link.link.url?.path}
          block
          asButton={link.isButton}
          beaconEvent={beaconEvent}
          className={link.isButton ? 'navbar__button' : 'navbar__link'}
          primary={link.primary}
          secondary={link.secondary}
          style={{ width: '100%' }}
          to={url}
          underline={isSubmenuLink ? 'hover' : 'none'}
        >
          {localizePhoneNumbers(link.title)}
        </Link>
      );
    };

    const renderLabel = (labelLink: LinkInterface) => (
      <DSNavbar.DropdownGroup
        key={labelLink.entityId}
        css={theme => ({
          [`@media (max-width: ${theme.navbar.collapseAt})`]: {
            flex: '0 1 50%'
          },

          '@media (max-width: 600px)': {
            flex: '0 1 100%'
          }
        })}
      >
        <p>{labelLink.title}</p>
        {labelLink.links.map(childLink =>
          renderItem(childLink, true, false, false)
        )}
      </DSNavbar.DropdownGroup>
    );

    const renderPlaceholder = () => (
      <Button
        secondary
        className="navbar__button navbar__button--placeholder"
      />
    );

    const renderDropdown = (menuItem: LinkInterface, isButton?: boolean) => (
      <DSNavbar.Dropdown
        css={{
          position: menuItem.fieldIsbigdropdown ? 'static' : 'relative',
          '& > div::before': {
            top: menuItem.fieldIsbigdropdown ? -20 : -3
          }
        }}
        toggle={toggleHandler => (
          <Button
            block
            asLink={!(menuItem.isButton || isButton)}
            className={`${
              menuItem.isButton || isButton ? 'navbar__button' : ''
              } ${menuItem.truncate ? 'navbar__button--truncate' : ''}`}
            onClick={() => {
              menuItem.fieldBeaconElementDetail &&
                sendBeaconEvent({
                  name: 'clickedMarketingPageElement',
                  elementLocation: MarketingPageElementLocation.nav,
                  elementDetail: menuItem.fieldBeaconElementDetail,
                  elementType: isButton
                    ? MarketingPageElementType.buttonsecondary
                    : MarketingPageElementType.link
                });
            }}
            onFocus={toggleHandler}
            onMouseOver={toggleHandler}
            primary={menuItem.primary}
            secondary={menuItem.secondary}
            style={{ position: 'relative' }}
          >
            {menuItem.title}
          </Button>
        )}
      >
        {menuItem.entityBundle === 'main-nav-links' &&
          menuItem.entityId === '2' &&
          langPrefix === 'en' ? (
            <DSNavbar.DropdownContainer
              css={theme => ({
                maxWidth: menuItem.fieldIsbigdropdown ? '55rem' : undefined,
                flexWrap: menuItem.fieldIsbigdropdown ? 'nowrap' : 'wrap',
                boxShadow: navbarBoxShadow,

                [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                  boxShadow: 'none',
                  display: 'block',
                  minWidth: 0
                },

                [`@media (max-width: ${mobileBreakpoint})`]: {
                  marginBottom: '-1rem'
                }
              })}
              // @ts-ignore
              displayLeft={false}
            >
              <div
                css={theme => ({
                  display: 'flex',
                  padding: '1rem 0',
                  width: '100%',

                  a: {
                    fontWeight: 400
                  },

                  [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                    flexWrap: 'wrap',
                    paddingTop: '.5rem'
                  }
                })}
              >
                {menuItem.links.map(dropdownLink =>
                  renderItem(dropdownLink, true, false, false)
                )}
              </div>
              <div
                css={theme => ({
                  display: 'flex',
                  flexWrap: 'wrap',
                  order: -1,
                  maxWidth: '45%',
                  width: '100%',
                  '> .ab-test__branch': {
                    flex: `1 1 ${addonLinks.length === 2 ? '100%' : '50%'}`
                  },
                  border: `1px solid ${lucidColors.coolGrey[5]}`,

                  [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                    display: 'none'
                  },

                  [`@media (max-width: ${mobileBreakpoint})`]: {
                    display: 'flex',
                    maxWidth: 'none',
                    width: 'auto',
                    marginLeft: '-2rem',
                    marginRight: '-2rem'
                  }
                })}
              >
                {addonLinks.map(
                  (
                    { entityId, title, fieldThumbnail, fieldAddonBlurb, link },
                    i
                  ) => (
                      /* eslint-disable-next-line react/jsx-indent */
                      <Link
                        key={entityId}
                        css={theme => ({
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'flex-start',
                          alignContent: 'center',
                          backgroundColor: lucidColors.coolGrey[5],
                          flex: `1 1 ${addonLinks.length === 2 ? '100%' : '50%'}`,
                          width: '100%',
                          border: '1px solid white',

                          '&:hover, &:hover:visited, &:focus, &:focus:visited': {
                            background: `${lucidColors.coolGrey[10]} !important`,
                            color: theme.links.default.default.color,
                            textDecoration: 'none !important'
                          },

                          [`@media (max-width: ${mobileBreakpoint})`]: {
                            padding: '1rem !important'
                          }
                        })}
                        to={link.url?.path!}
                      >
                        <div
                          css={theme => ({
                            flex: '0 1 100%',
                            fontWeight: theme.fontWeights.bold,
                            lineHeight: 1.5,
                            marginBottom: '.5rem'
                          })}
                        >
                          {title}
                        </div>
                        <div
                          css={{
                            flex: '0 1 2.5rem',
                            alignSelf: 'center',
                            marginRight: '1rem',
                            display: showImage(i) ? 'block' : 'none'
                          }}
                        >
                          <LazyLoad>
                            <img alt={title} src={fieldThumbnail} />
                          </LazyLoad>
                        </div>
                        <div
                          css={theme => ({
                            flex: 1,
                            lineHeight: 1.5,
                            fontWeight: theme.fontWeights.body
                          })}
                        >
                          {fieldAddonBlurb}
                        </div>
                      </Link>
                    )
                )}
              </div>
            </DSNavbar.DropdownContainer>
          ) : (
            <DSNavbar.DropdownContainer
              css={{
                a: {
                  fontWeight: 400
                }
              }}
            >
              {menuItem.links.map(dropdownLink =>
                renderItem(dropdownLink, true, false, false)
              )}
            </DSNavbar.DropdownContainer>
          )}
      </DSNavbar.Dropdown>
    );

    let contents;
    if (type === LinkType.PLACEHOLDER) {
      contents = renderPlaceholder();
    }
    if (type === LinkType.DROPDOWN) {
      contents = renderDropdown(link, isButton);
    }
    if (type === LinkType.LABEL) {
      contents = renderLabel(link);
    }
    if (type === LinkType.MARKETO) {
      contents = renderMarketoButton(link);
    }
    if (type === LinkType.LINK) {
      contents = renderLink(link, isSubmenuLink);
    }

    if (isTopLevel) {
      return <DSNavbar.Item key={link.entityId}>{contents}</DSNavbar.Item>;
    } else {
      return contents;
    }
  };

  const menuItems = menu.concat(isBrowser() ? navButtons : placeholderButtons);

  return (
    <Box className="lucidchart-navbar">
      <NavbarWrapper sticky>
        <DSNavbar>
          <NavbarContext.Consumer>
            {({ expanded }) => {
              if (expanded !== isOpen) {
                setIsOpen(expanded);
              }
              return (
                <div className="navbar__container">
                  <DSNavbar.Container>
                    <DSNavbar.Brand>
                      <Link
                        beaconEvent={{
                          name: 'clickedMarketingPageElement',
                          elementLocation: MarketingPageElementLocation.nav,
                          elementDetail: 'logo',
                          elementType: MarketingPageElementType.image
                        }}
                        to={LOGO_LINK}
                      >
                        <img
                          alt={title}
                          className="navbar__logo-image"
                          src={LOGO}
                          style={{ width: '170px' }}
                        />
                      </Link>
                    </DSNavbar.Brand>
                    <DSNavbar.Toggle>{i18n('mktg-nav-menu')}</DSNavbar.Toggle>
                    <div
                      css={theme => ({
                        display: 'none',
                        height: '1rem',
                        flex: '0 1 100%',
                        [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                          display: expanded ? 'block' : 'none'
                        }
                      })}
                    />
                    <div
                      css={theme => ({
                        display: 'none',
                        flex: '0 1 45%',
                        backgroundColor: lucidColors.coolGrey[5],
                        boxShadow: navbarBoxShadow,
                        border: `1px solid ${lucidColors.coolGrey[5]}`,
                        borderRight: 0,
                        [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                          display: expanded ? 'block' : 'none'
                        },
                        [`@media (max-width: ${mobileBreakpoint})`]: {
                          display: 'none'
                        }
                      })}
                    >
                      {addonLinks.map(
                        ({
                          entityId,
                          title,
                          fieldThumbnail,
                          fieldAddonBlurb,
                          link
                        }) => (
                            /* eslint-disable-next-line react/jsx-indent */
                            <Link
                              key={entityId}
                              css={theme => ({
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'flex-start',
                                alignContent: 'center',
                                backgroundColor: lucidColors.coolGrey[5],
                                flex: 1,
                                borderBottom: '1px solid white',
                                padding: '1.5rem',

                                '&:hover, &:hover:visited, &:focus, &:focus:visited': {
                                  background: `${lucidColors.coolGrey[10]} !important`,
                                  color: theme.links.default.default.color,
                                  textDecoration: 'none !important'
                                }
                              })}
                              to={link.url?.path!}
                            >
                              <div
                                css={theme => ({
                                  flex: '0 1 100%',
                                  fontWeight: theme.fontWeights.bold,
                                  lineHeight: 1.5,
                                  marginBottom: '.5rem'
                                })}
                              >
                                {title}
                              </div>
                              <div
                                css={{
                                  flex: '0 1 2.5rem',
                                  alignSelf: 'center',
                                  marginRight: '1rem',

                                  '@media (max-width: 600px)': {
                                    display: 'none'
                                  }
                                }}
                              >
                                <LazyLoad>
                                  <img alt={title} src={fieldThumbnail} />
                                </LazyLoad>
                              </div>
                              <div
                                css={theme => ({
                                  flex: 1,
                                  lineHeight: 1.5,
                                  fontWeight: theme.fontWeights.body,

                                  '@media (max-width: 600px)': {
                                    flex: '0 1 100%'
                                  }
                                })}
                              >
                                {fieldAddonBlurb}
                              </div>
                            </Link>
                          )
                      )}
                    </div>
                    <DSNavbar.InnerContainer
                      css={theme => ({
                        '> li:first-of-type': {
                          borderTop: 0
                        },

                        [`@media (max-width: ${theme.navbar.collapseAt})`]: {
                          boxShadow: '2px 2px 2px rgba(196, 196, 196, 0.5)',
                          flex: 1
                        }
                      })}
                    >
                      {menuItems.map(item => renderItem(item))}
                    </DSNavbar.InnerContainer>
                    <DSNavbar.Border
                      isHidden={alwaysScrolled ? false : !isScrolled}
                    />
                  </DSNavbar.Container>
                </div>
              );
            }}
          </NavbarContext.Consumer>
        </DSNavbar>
      </NavbarWrapper>
    </Box>
  );
};

export default Navbar;
